import { Combobox } from '@ordo/ui/combobox'
import { Input } from '@ordo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { AsyncCombobox } from 'compositions/AsyncCombobox'
import { SenderSelector } from 'compositions/SenderSelector'
import { useSubscriptionTopics, useTemplate } from 'hooks/api'
import { useAsyncLoader } from 'hooks/useAsyncLoader'
import { useMemo } from 'react'
import type { CommunicationMethod } from 'types/communications'
import type { Template } from 'types/templates'

import { type Recipient, RecipientSelector } from './recipient-selector'

export interface CommunicationFormData {
  name: string
  method: CommunicationMethod
  subject: string
  templateId: string
  topicId: string
  senderId: string
}

export function CommunicationFormFields({
  data,
  onChange,
  recipients,
  onAddRecipient,
  onRemoveRecipient,
}: {
  data: CommunicationFormData
  onChange: (field: keyof CommunicationFormData, value: string) => void
  recipients: Recipient[]
  onAddRecipient: (recipient: Recipient) => void
  onRemoveRecipient: (recipient: Recipient) => void
}) {
  const loadTemplates = useAsyncLoader<Template>('/api/templates', { active: true })
  const { data: selectedTemplate } = useTemplate(data.templateId || undefined)
  const selectedTemplateItem = useMemo<Template | null>(
    () => (data.templateId && selectedTemplate?.id === data.templateId ? selectedTemplate : null),
    [data.templateId, selectedTemplate],
  )

  const { data: topics } = useSubscriptionTopics()
  const topicOptions = (topics ?? []).map((t) => ({
    value: t.id,
    label: t.isDefault ? `${t.name} (default)` : t.name,
  }))

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Name */}
        <div className="space-y-1.5">
          <label htmlFor="commName" className="text-sm font-medium text-muted-foreground">
            Name
          </label>
          <Input id="commName" value={data.name} onChange={(e) => onChange('name', e.target.value)} />
        </div>

        {/* Method */}
        <div className="space-y-1.5">
          <span className="text-sm font-medium text-muted-foreground">Delivery Method</span>
          <Select
            value={data.method}
            onValueChange={(v) => {
              onChange('method', v)
              onChange('senderId', '')
            }}>
            <SelectTrigger className="w-full text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="EMAIL">Email</SelectItem>
              <SelectItem value="SMS">SMS</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Template selector */}
      <div className="space-y-1.5">
        <span className="text-sm font-medium text-muted-foreground">Template</span>
        <AsyncCombobox<Template>
          value={selectedTemplateItem}
          onChange={(item) => onChange('templateId', item?.id ?? '')}
          load={loadTemplates}
          getKey={(item) => item.id}
          getLabel={(item) => item.name}
          placeholder="Select a template"
          searchPlaceholder="Search templates…"
          emptyMessage="No templates available"
        />
      </div>

      {/* Subscription topic */}
      <div className="space-y-1.5">
        <span className="text-sm font-medium text-muted-foreground">Subscription topic</span>
        <Combobox
          options={topicOptions}
          value={data.topicId}
          onValueChange={(v) => onChange('topicId', v)}
          placeholder="Use workspace default"
          searchPlaceholder="Search topics..."
          emptyMessage="No topics — one will be created on send."
        />
        <p className="text-xs text-muted-foreground">
          Recipients who have opted out of this topic (or all marketing) will be skipped.
        </p>
      </div>

      {/* Recipients */}
      <div className="space-y-1.5">
        <span className="text-sm font-medium text-muted-foreground">Recipients</span>
        <RecipientSelector recipients={recipients} onAdd={onAddRecipient} onRemove={onRemoveRecipient} />
      </div>

      {/* Saved senders are filtered by the selected channel. */}
      <SenderSelector method={data.method} value={data.senderId} onChange={(v) => onChange('senderId', v)} />

      {/* Subject — only for EMAIL */}
      {data.method === 'EMAIL' && (
        <div className="space-y-1.5">
          <label htmlFor="commSubject" className="text-sm font-medium text-muted-foreground">
            Subject
          </label>
          <Input id="commSubject" value={data.subject} onChange={(e) => onChange('subject', e.target.value)} />
        </div>
      )}
    </div>
  )
}
